/**
 * AdminIntakeInbox — Pre-Admission Intake Inbox (iter 167).
 *
 * Ships the ArcHouse-parity intake surface: upload a referral packet PDF,
 * Cara extracts structured clinical + insurance fields (Vertex/BAA), the
 * coordinator reviews + edits, then accepts (promotes to a ReferralWorkflow
 * → triggers matching) or rejects with a reason.
 *
 * Route:  /admin/intake        Admin-only (403 for anyone else).
 * Live:   POST /api/intake/upload · GET /api/intake · POST /api/intake/{id}/accept·reject·re-extract
 *
 * Design intent — mirror ArcHouse's core value prop:
 *   1. Fast upload (drag-drop, <200ms server response)
 *   2. AI extraction runs in background with live status pill
 *   3. Editable extracted fields with confidence banner
 *   4. Records / consent gaps surfaced by the AI as "gaps"
 *   5. One click to accept → matching engine picks it up
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Loader2, FileText, CheckCircle2, XCircle, Clock, RefreshCw,
  AlertTriangle, Sparkles, Download, User, Stethoscope, ClipboardList, Building2, ShieldAlert,
  ClipboardCheck, Send, Zap, Users, ScanLine,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { ReferralTimeline } from '../components/admin/ReferralTimeline';
import { HieRecordsPanel } from '../components/hie/HieRecordsPanel';
import { Shield, ShieldCheck, Inbox } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Shield, label: 'Compliance', href: '/admin/compliance' },
  { icon: ShieldCheck, label: 'AI Validation', href: '/admin/ai-validation' },
  { icon: Inbox, label: 'Intake Inbox', href: '/admin/intake', active: true },
  { icon: Building2, label: 'Referral Sources', href: '/admin/referral-sources' },
  { icon: Users, label: 'CRM', href: '/admin/crm' },
  { icon: ShieldCheck, label: 'HIE Console', href: '/admin/hie' },
];

const STATUS_STYLE = {
  extracting:         { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',   Icon: Loader2,        spin: true,  label: 'Extracting' },
  extracted:          { cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30',         Icon: Sparkles,       spin: false, label: 'Ready to review' },
  extraction_failed:  { cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30',      Icon: AlertTriangle,  spin: false, label: 'Extraction failed' },
  accepted:           { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: CheckCircle2, spin: false, label: 'Accepted' },
  rejected:           { cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30',   Icon: XCircle,        spin: false, label: 'Rejected' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_STYLE[status] || STATUS_STYLE.extracting;
  const { Icon, cls, spin, label } = cfg;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}
      data-testid={`intake-status-${status}`}
    >
      <Icon className={`h-3 w-3 ${spin ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
}

function ConfidenceMeter({ value }) {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 80 ? 'bg-emerald-500' : pct >= 55 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2" data-testid="confidence-meter">
      <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-slate-400">{pct}%</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Upload zone
// ────────────────────────────────────────────────────────────────────────────

function UploadZone({ onUploaded, getAuthHeader }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF referral packets are accepted right now.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File is too large (25 MB max).');
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/intake/upload`, form, {
        ...getAuthHeader(),
        headers: {
          ...(getAuthHeader().headers || {}),
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Uploaded — Cara is extracting fields now.');
      onUploaded?.(data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [getAuthHeader, onUploaded]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) upload(f);
      }}
      className={`rounded-2xl border-2 border-dashed transition-colors p-8 flex flex-col items-center justify-center text-center ${
        dragOver
          ? 'border-cn-primary bg-cn-primary/5'
          : 'border-slate-700 bg-slate-900/40'
      }`}
      data-testid="intake-upload-zone"
    >
      <Upload className="h-8 w-8 text-slate-500 mb-3" />
      <p className="text-sm font-semibold text-slate-200">Drop a referral packet PDF here</p>
      <p className="text-xs text-slate-500 mt-1">
        Cara extracts patient, insurance, ICD-10, ordered services and gaps automatically.
      </p>
      <div className="mt-4">
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="intake-file-input"
        />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid="intake-upload-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Choose PDF
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Detail drawer
// ────────────────────────────────────────────────────────────────────────────

function IntakeDetailDrawer({ intakeId, onClose, onChanged, getAuthHeader }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [edits, setEdits] = useState(null);
  // Iter 171 — HIE / Metriport
  const [hiePull, setHiePull] = useState(null);
  const [hieBusy, setHieBusy] = useState(false);
  const [acceptWithConsent, setAcceptWithConsent] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/intake/${intakeId}`, getAuthHeader());
      setDetail(data);
      setEdits(data.extracted || {});
    } catch (e) {
      toast.error('Could not load intake record');
    } finally {
      setLoading(false);
    }
  }, [intakeId, getAuthHeader]);

  const loadHie = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hie/pull/${intakeId}`, getAuthHeader());
      setHiePull(data.pull || null);
    } catch (e) {
      // 404 or not-found — silently leave hiePull null
      setHiePull(null);
    }
  }, [intakeId, getAuthHeader]);

  useEffect(() => { load(); loadHie(); }, [load, loadHie]);

  // Live-poll while extracting
  useEffect(() => {
    if (detail?.status !== 'extracting') return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [detail?.status, load]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/intake/${intakeId}`, { extracted: edits }, getAuthHeader());
      toast.success('Saved edits');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const reExtract = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/intake/${intakeId}/re-extract`, {}, getAuthHeader());
      toast.success('Re-running Cara extraction…');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-extract failed');
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      // Save any local edits first so the promoted referral is authoritative.
      await axios.patch(`${API}/intake/${intakeId}`, { extracted: edits }, getAuthHeader());
      const { data } = await axios.post(
        `${API}/intake/${intakeId}/accept`,
        // Iter 171 — pass HIE consent alongside the accept if the coordinator
        // ticked the box. The backend stamps the consent + fires the pull.
        { hie_consent: !!acceptWithConsent, hie_consent_source: 'intake_form' },
        getAuthHeader(),
      );
      toast.success(`Accepted — referral workflow ${data.workflow_id.slice(0, 8)}… created`);
      if (acceptWithConsent) toast.info('HIE pull queued (Metriport).');
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Accept failed');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 2) {
      toast.error('Please provide a reason (2+ chars)');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/intake/${intakeId}/reject`, { reason: rejectReason.trim() }, getAuthHeader());
      toast.success('Referral rejected');
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  // Iter 171 — HIE handlers
  const captureConsent = async () => {
    setHieBusy(true);
    try {
      await axios.post(
        `${API}/hie/consent/${intakeId}`,
        { consented: true, source: 'intake_form' },
        getAuthHeader(),
      );
      toast.success('HIE consent captured');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not capture consent');
    } finally {
      setHieBusy(false);
    }
  };

  const pullHie = async () => {
    setHieBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/hie/pull/${intakeId}`,
        { force: false },
        getAuthHeader(),
      );
      setHiePull(data.pull);
      if (data.pull?.status === 'completed') {
        toast.success('HIE pull completed');
      } else if (data.pull?.status === 'processing') {
        toast.info('HIE pull started — check back shortly');
      }
      // Refresh in case a webhook lands quickly (live mode).
      setTimeout(loadHie, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'HIE pull failed');
    } finally {
      setHieBusy(false);
    }
  };

  const patch = (path, value) => {
    setEdits((prev) => {
      const next = { ...(prev || {}) };
      const [section, key] = path.split('.');
      if (key === undefined) {
        next[section] = value;
      } else {
        next[section] = { ...(next[section] || {}), [key]: value };
      }
      return next;
    });
  };

  if (loading || !detail) {
    return (
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center"
        onClick={onClose}
        data-testid="intake-detail-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const e = edits || {};
  const patient = e.patient || {};
  const insurance = e.insurance || {};
  const diagnoses = e.diagnoses || {};
  const facility = e.referring_facility || {};
  const orderedList = (e.ordered_services || []).join(', ');
  const gaps = e.gaps || [];

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex justify-end"
      onClick={(ev) => ev.target === ev.currentTarget && onClose()}
      data-testid="intake-detail-drawer"
    >
      <div className="bg-slate-950 border-l border-slate-800 w-full max-w-3xl h-full overflow-y-auto shadow-2xl">
        <header className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800 p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-500" />
              {detail.original_filename}
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <StatusBadge status={detail.status} />
              {typeof detail.extraction_confidence === 'number' && detail.status === 'extracted' && (
                <ConfidenceMeter value={detail.extraction_confidence} />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`${API}/intake/${intakeId}/file`, '_blank')}
              data-testid="intake-download-pdf"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Download PDF
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="intake-close-drawer">
              Close
            </Button>
          </div>
        </header>

        <div className="p-5 space-y-6">
          {detail.status === 'extraction_failed' && (
            <div
              className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200"
              data-testid="intake-extraction-error"
            >
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" /> Extraction failed
              </div>
              {(detail.extraction_errors || []).map((err, i) => (
                <p key={i} className="mt-1 text-xs">{err}</p>
              ))}
              {detail.needs_ocr && (
                <p className="mt-2 text-xs italic">
                  This looks like a scanned/image PDF. OCR isn&apos;t wired yet — fill fields manually below, or re-upload a text-selectable version.
                </p>
              )}
            </div>
          )}

          {e.clinical_summary && (
            <section
              className="rounded-xl border border-cn-primary/20 bg-cn-primary/5 p-4"
              data-testid="intake-clinical-summary"
            >
              <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-cn-primary">
                <Sparkles className="h-3.5 w-3.5" /> Cara summary
              </h3>
              <p className="mt-2 text-sm text-slate-200 italic leading-relaxed">
                &ldquo;{e.clinical_summary}&rdquo;
              </p>
            </section>
          )}

          {detail.status === 'extracted' && (detail.extraction_confidence ?? 1) < 0.55 && (
            <div
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 flex items-center gap-2"
              data-testid="intake-low-confidence-warn"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                Cara&apos;s confidence on this extraction is low
                ({Math.round((detail.extraction_confidence || 0) * 100)}%).
                Please review every field carefully before accepting.
              </span>
            </div>
          )}

          {gaps.length > 0 && (
            <section
              className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4"
              data-testid="intake-gaps"
            >
              <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-300">
                <ShieldAlert className="h-3.5 w-3.5" /> Missing / gaps
              </h3>
              <ul className="mt-2 space-y-0.5 text-xs text-slate-200">
                {gaps.map((g, i) => <li key={i}>• {g}</li>)}
              </ul>
            </section>
          )}

          <Section title="Patient" icon={User} testId="section-patient">
            <Field label="Name"    value={patient.name}    onChange={(v) => patch('patient.name', v)} />
            <Field label="DOB"     value={patient.dob}     onChange={(v) => patch('patient.dob', v)} />
            <Field label="Sex"     value={patient.sex}     onChange={(v) => patch('patient.sex', v)} />
            <Field label="Phone"   value={patient.phone}   onChange={(v) => patch('patient.phone', v)} />
            <Field label="Address" value={patient.address} onChange={(v) => patch('patient.address', v)} full />
            <Field label="MRN"     value={patient.mrn}     onChange={(v) => patch('patient.mrn', v)} />
          </Section>

          <Section title="Insurance" icon={ClipboardList} testId="section-insurance">
            <Field label="Payer"       value={insurance.payer}         onChange={(v) => patch('insurance.payer', v)} />
            <Field label="Payer type"  value={insurance.payer_type}    onChange={(v) => patch('insurance.payer_type', v)} />
            <Field label="Member ID"   value={insurance.member_id}     onChange={(v) => patch('insurance.member_id', v)} />
            <Field label="MBI"         value={insurance.mbi}           onChange={(v) => patch('insurance.mbi', v)} />
            <Field label="Plan"        value={insurance.plan}          onChange={(v) => patch('insurance.plan', v)} />
            <Field label="Effective"   value={insurance.effective_date} onChange={(v) => patch('insurance.effective_date', v)} />
          </Section>

          <Section title="Clinical" icon={Stethoscope} testId="section-clinical">
            <Field
              label="Primary diagnosis"
              value={diagnoses.primary}
              onChange={(v) => patch('diagnoses.primary', v)}
              full
            />
            <Field
              label="ICD-10 codes"
              value={(diagnoses.icd10 || []).join(', ')}
              onChange={(v) => patch('diagnoses.icd10', v.split(',').map((s) => s.trim()).filter(Boolean))}
            />
            <Field
              label="Comorbidities"
              value={(diagnoses.comorbidities || []).join(', ')}
              onChange={(v) => patch('diagnoses.comorbidities', v.split(',').map((s) => s.trim()).filter(Boolean))}
              full
            />
            <Field
              label="Ordered services"
              value={orderedList}
              onChange={(v) => patch('ordered_services', v.split(',').map((s) => s.trim()).filter(Boolean))}
              full
              hint="comma-separated · e.g. skilled_nursing, physical_therapy"
            />
            <Field
              label="Medications"
              value={(e.medications || []).join(', ')}
              onChange={(v) => patch('medications', v.split(',').map((s) => s.trim()).filter(Boolean))}
              full
            />
            <Field
              label="Allergies"
              value={(e.allergies || []).join(', ')}
              onChange={(v) => patch('allergies', v.split(',').map((s) => s.trim()).filter(Boolean))}
              full
            />
            <div className="flex flex-wrap gap-4 mt-2 text-xs">
              <label className="inline-flex items-center gap-1.5 text-slate-300">
                <input
                  type="checkbox"
                  checked={!!e.homebound}
                  onChange={(ev) => patch('homebound', ev.target.checked)}
                  data-testid="intake-homebound"
                />
                Homebound
              </label>
              <label className="inline-flex items-center gap-1.5 text-slate-300">
                <input
                  type="checkbox"
                  checked={!!e.f2f_encounter_present}
                  onChange={(ev) => patch('f2f_encounter_present', ev.target.checked)}
                  data-testid="intake-f2f"
                />
                F2F encounter attached
              </label>
              <label className="inline-flex items-center gap-1.5 text-slate-300">
                <input
                  type="checkbox"
                  checked={!!e.hospice}
                  onChange={(ev) => patch('hospice', ev.target.checked)}
                  data-testid="intake-hospice"
                />
                Hospice election
              </label>
            </div>
          </Section>

          <Section title="Referring facility" icon={Building2} testId="section-facility">
            <Field label="Facility"     value={facility.name}         onChange={(v) => patch('referring_facility.name', v)} full />
            <Field label="Contact"      value={facility.contact_name} onChange={(v) => patch('referring_facility.contact_name', v)} />
            <Field label="Phone"        value={facility.phone}        onChange={(v) => patch('referring_facility.phone', v)} />
            <Field label="Fax"          value={facility.fax}          onChange={(v) => patch('referring_facility.fax', v)} />
            <Field label="NPI"          value={facility.npi}          onChange={(v) => patch('referring_facility.npi', v)} />
          </Section>

          <RecordsChecklist
            intakeId={intakeId}
            items={detail.records_checklist}
            onChanged={load}
            getAuthHeader={getAuthHeader}
          />

          <HieRecordsPanel
            pull={hiePull}
            onRefresh={pullHie}
            canConsent={detail.status !== 'rejected'}
            hasConsent={!!detail.hie_consent?.consented}
            onCaptureConsent={captureConsent}
            busy={hieBusy}
          />

          <section data-testid="intake-timeline-section">
            <ReferralTimeline
              events={detail.events}
              testId={`intake-timeline-${intakeId}`}
              title="Lifecycle"
            />
          </section>

          {rejectMode ? (
            <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-4">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-rose-300">
                  Reason for reject
                </span>
                <textarea
                  autoFocus
                  rows={3}
                  value={rejectReason}
                  onChange={(ev) => setRejectReason(ev.target.value)}
                  className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. Out of service area · payer not accepted · duplicate referral"
                  data-testid="intake-reject-reason"
                />
              </label>
              <div className="mt-3 flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRejectMode(false)} disabled={busy} data-testid="intake-reject-cancel">
                  Back
                </Button>
                <Button
                  onClick={reject}
                  disabled={busy || rejectReason.trim().length < 2}
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                  data-testid="intake-reject-confirm"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm reject'}
                </Button>
              </div>
            </div>
          ) : (
            detail.status !== 'accepted' && detail.status !== 'rejected' && (
              <>
                {!detail.hie_consent?.consented && (
                  <label
                    className="flex items-start gap-2 rounded-lg border border-cn-primary/30 bg-cn-primary/5 p-3 text-xs text-slate-200 cursor-pointer"
                    data-testid="accept-with-hie-consent-label"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={acceptWithConsent}
                      onChange={(ev) => setAcceptWithConsent(ev.target.checked)}
                      data-testid="accept-with-hie-consent"
                    />
                    <span>
                      <span className="font-semibold text-slate-100">Patient consented to HIE queries.</span>{' '}
                      When accepted, Cara will pull records via Metriport (CommonWell + Carequality).
                      Uncheck if consent has not yet been obtained.
                    </span>
                  </label>
                )}
                <div className="sticky bottom-0 bg-slate-950/95 backdrop-blur border-t border-slate-800 -mx-5 -mb-5 px-5 py-4 flex items-center gap-2 justify-between">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={reExtract} disabled={busy} data-testid="intake-re-extract">
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? 'animate-spin' : ''}`} /> Re-extract
                  </Button>
                  <Button variant="outline" onClick={save} disabled={busy} data-testid="intake-save-edits">
                    Save edits
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="text-rose-300 border-rose-500/40 hover:bg-rose-500/10"
                    onClick={() => setRejectMode(true)}
                    disabled={busy}
                    data-testid="intake-reject-btn"
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1.5" />
                    Reject
                  </Button>
                  <Button
                    onClick={accept}
                    disabled={busy || detail.status === 'extracting'}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="intake-accept-btn"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        Accept &amp; match
                      </>
                    )}
                  </Button>
                </div>
              </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid={testId}>
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {title}
      </h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {children}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Records checklist — iter 168
// ────────────────────────────────────────────────────────────────────────────

const RECORDS_STATUS_STYLE = {
  needed:         { cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30',   label: 'Needed' },
  requested:      { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',   label: 'Requested' },
  received:       { cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30',         label: 'Received' },
  verified:       { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', label: 'Verified' },
  not_applicable: { cls: 'bg-slate-700/30 text-slate-500 border-slate-600/30',   label: 'N/A' },
};

const RECORDS_STATUSES = ['needed', 'requested', 'received', 'verified', 'not_applicable'];

function RecordsChecklist({ intakeId, items, onChanged, getAuthHeader }) {
  const [busyKey, setBusyKey] = useState(null);

  const patchItem = async (key, status) => {
    setBusyKey(`patch:${key}`);
    try {
      await axios.patch(
        `${API}/intake/${intakeId}/records/${key}`,
        { status },
        getAuthHeader(),
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyKey(null);
    }
  };

  const requestItem = async (key) => {
    setBusyKey(`request:${key}`);
    try {
      const { data } = await axios.post(
        `${API}/intake/${intakeId}/records/${key}/request`,
        {},
        getAuthHeader(),
      );
      toast.success(
        data.delivered_via === 'resend'
          ? 'Chase-up email sent to the referring facility'
          : 'Chase-up logged (MOCK — Resend not configured or no contact email)',
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Request failed');
    } finally {
      setBusyKey(null);
    }
  };

  const items_ = items || [];
  const total = items_.length;
  const gotten = items_.filter((c) => c.status === 'received' || c.status === 'verified').length;

  return (
    <section
      className="rounded-xl border border-slate-800 bg-slate-900/40 p-4"
      data-testid="records-checklist-section"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
          <ClipboardCheck className="h-3.5 w-3.5" /> Records checklist
        </h3>
        <span className="text-xs text-slate-500 tabular-nums" data-testid="records-progress">
          {gotten} / {total} received
        </span>
      </div>
      {total === 0 ? (
        <p className="text-xs text-slate-500 italic">Waiting for extraction to seed the checklist…</p>
      ) : (
        <ul className="space-y-1.5">
          {items_.map((c) => {
            const style = RECORDS_STATUS_STYLE[c.status] || RECORDS_STATUS_STYLE.needed;
            const canRequest = c.status === 'needed';
            const busy = busyKey === `patch:${c.key}` || busyKey === `request:${c.key}`;
            return (
              <li
                key={c.key}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/40 transition-colors"
                data-testid={`records-item-${c.key}`}
              >
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium min-w-[90px] justify-center ${style.cls}`}
                  data-testid={`records-status-${c.key}`}
                >
                  {style.label}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-100 truncate">{c.label}</p>
                  {c.notes && (
                    <p className="text-[11px] text-slate-500 italic truncate">&ldquo;{c.notes}&rdquo;</p>
                  )}
                </div>
                <select
                  value={c.status}
                  disabled={busy}
                  onChange={(e) => patchItem(c.key, e.target.value)}
                  className="bg-slate-900 border border-slate-800 rounded-md px-1.5 py-1 text-xs text-slate-200"
                  data-testid={`records-select-${c.key}`}
                >
                  {RECORDS_STATUSES.map((s) => (
                    <option key={s} value={s}>{RECORDS_STATUS_STYLE[s].label}</option>
                  ))}
                </select>
                {canRequest && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => requestItem(c.key)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-cn-primary hover:text-cn-primary/80 disabled:opacity-40 px-2 py-1"
                    title="Send email chase-up to the referring facility"
                    data-testid={`records-request-${c.key}`}
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    Request
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Field({ label, value, onChange, full, hint }) {
  return (
    <label className={`block ${full ? 'col-span-2' : ''}`}>
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        className="mt-0.5 w-full bg-slate-900 border border-slate-800 rounded-md px-2.5 py-1.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-cn-primary/60"
        placeholder="—"
      />
      {hint && <span className="text-[10px] text-slate-600 italic mt-0.5 block">{hint}</span>}
    </label>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export default function AdminIntakeInbox() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [todayOnly, setTodayOnly] = useState(false);
  const [todaySummary, setTodaySummary] = useState(null);
  const [faxStatus, setFaxStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (todayOnly) params.today = true;
      const { data } = await axios.get(`${API}/intake`, { ...getAuthHeader(), params });
      setRows(data.rows || []);
      setSummary(data.summary || {});
      setTodaySummary(data.today || null);
    } catch (e) {
      toast.error('Could not load intake inbox');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, statusFilter, todayOnly]);

  useEffect(() => { load(); }, [load]);

  // Load Documo status once — used to render "Fax at 555-…" or a MOCK pill.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/documo/status`, getAuthHeader());
        if (!cancelled) setFaxStatus(data);
      } catch (e) {
        // silently — non-admin roles get 403 which is fine
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  // Live-refresh while any row is still extracting
  useEffect(() => {
    if (!rows.some((r) => r.status === 'extracting')) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [rows, load]);

  return (
    <DashboardShell navItems={ADMIN_NAV}>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="admin-intake-inbox">
        <header className="flex flex-wrap items-end gap-4 justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100 tracking-tight">
              Pre-Admission Intake Inbox
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">
              Drop a hospital referral packet PDF · Cara pulls patient, insurance, ICD-10, ordered services · one click promotes to matching.
            </p>
            {faxStatus && (
              <div className="mt-2 flex items-center gap-2" data-testid="documo-status-pill">
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                    faxStatus.mode === 'live'
                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                      : 'bg-slate-500/15 text-slate-400 border-slate-600/30'
                  }`}
                  title={
                    faxStatus.mode === 'live'
                      ? 'Documo eFax channel is live — inbound faxes will appear in this inbox.'
                      : 'Documo eFax channel is in MOCK mode. Set DOCUMO_ENABLED=true + API key + BAA to go live.'
                  }
                >
                  {faxStatus.mode === 'live' ? '📠 Fax intake live' : '📠 Fax intake · MOCK'}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs">
            <SummaryPill label="Extracting" n={summary.extracting || 0} tone="amber" />
            <SummaryPill label="Ready" n={summary.extracted || 0} tone="sky" />
            <SummaryPill label="Accepted" n={summary.accepted || 0} tone="emerald" />
            <SummaryPill label="Rejected" n={summary.rejected || 0} tone="slate" />
          </div>
        </header>

        <UploadZone onUploaded={load} getAuthHeader={getAuthHeader} />

        {/* Iter 169 · Same-Day Admits — one-click filter to only show
            in-flight packets with the time-to-admit clock running today.
            Badge shows the breach count so BD can spot problems fast. */}
        {todaySummary && todaySummary.in_flight > 0 && (
          <div
            className="rounded-2xl border border-slate-800 bg-gradient-to-r from-amber-500/10 to-transparent p-4 flex items-center justify-between gap-4"
            data-testid="same-day-admit-banner"
          >
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-amber-400" />
              <div>
                <p className="text-sm font-semibold text-slate-100">
                  {todaySummary.in_flight} packet{todaySummary.in_flight === 1 ? '' : 's'} on the clock today
                  {todaySummary.breach > 0 && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 text-xs font-medium border border-rose-500/40"
                      data-testid="same-day-breach-badge"
                    >
                      <AlertTriangle className="h-3 w-3" /> {todaySummary.breach} breach
                    </span>
                  )}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Warn at {todaySummary.warn_threshold_hours}h · Breach at {todaySummary.breach_threshold_hours}h.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setTodayOnly((v) => !v)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                todayOnly
                  ? 'bg-cn-primary text-white'
                  : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
              }`}
              data-testid="same-day-filter-toggle"
            >
              {todayOnly ? 'Showing today only · click to clear' : 'Show today only'}
            </button>
          </div>
        )}

        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-slate-200">Recent referrals</h2>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-900 border border-slate-800 rounded-md px-2 py-1 text-xs text-slate-200"
              data-testid="intake-status-filter"
            >
              <option value="">All statuses</option>
              <option value="extracting">Extracting</option>
              <option value="extracted">Ready to review</option>
              <option value="extraction_failed">Extraction failed</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="intake-empty">
              No referrals yet. Drop a PDF above to get started.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                  <th className="px-4 py-2 font-medium">Patient</th>
                  <th className="px-4 py-2 font-medium">Diagnosis</th>
                  <th className="px-4 py-2 font-medium">Payer</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Received</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const e = r.extracted || {};
                  const patientName = e.patient?.name || r.original_filename || 'Unknown';
                  const dx = e.diagnoses?.primary || '—';
                  const payer = e.insurance?.payer || '—';
                  const sla = r.sla_state;   // ok | warn | breach | closed
                  const rowCls = sla === 'breach'
                    ? 'border-rose-500/40 bg-rose-500/5 hover:bg-rose-500/10'
                    : sla === 'warn'
                    ? 'border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10'
                    : 'border-slate-800/60 hover:bg-slate-800/30';
                  return (
                    <tr
                      key={r.id}
                      className={`border-b cursor-pointer transition-colors ${rowCls}`}
                      onClick={() => setOpenId(r.id)}
                      data-testid={`intake-row-${r.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-100 flex items-center gap-1.5">
                          {patientName}
                          {r.source === 'fax' && (
                            <span className="text-[9px] uppercase tracking-wide bg-slate-800 text-slate-400 rounded px-1 py-0.5" title="Received via Documo eFax">FAX</span>
                          )}
                          {r.used_ocr && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide bg-indigo-500/15 text-indigo-300 rounded px-1 py-0.5 border border-indigo-500/30"
                              title="Fields extracted via multimodal OCR (scanned PDF)"
                              data-testid={`intake-ocr-badge-${r.id}`}
                            >
                              <ScanLine className="h-2.5 w-2.5" /> OCR
                            </span>
                          )}
                        </div>
                        {e.patient?.dob && <div className="text-xs text-slate-500">DOB {e.patient.dob}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{dx}</td>
                      <td className="px-4 py-3 text-slate-300">{payer}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                        {sla === 'warn' && (
                          <span
                            className="ml-1.5 text-[10px] font-medium text-amber-300"
                            data-testid={`intake-sla-warn-${r.id}`}
                            title={`On the clock: ${r.age_hours}h`}
                          >
                            ⏱ {r.age_hours}h
                          </span>
                        )}
                        {sla === 'breach' && (
                          <span
                            className="ml-1.5 text-[10px] font-semibold text-rose-300"
                            data-testid={`intake-sla-breach-${r.id}`}
                            title={`SLA breach: ${r.age_hours}h`}
                          >
                            ⚠ {r.age_hours}h BREACH
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        <span title={r.created_at}>
                          <Clock className="inline h-3 w-3 mr-1" />
                          {relativeTime(r.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(ev) => { ev.stopPropagation(); setOpenId(r.id); }}
                          data-testid={`intake-open-${r.id}`}
                        >
                          Review
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {openId && (
        <IntakeDetailDrawer
          intakeId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
          getAuthHeader={getAuthHeader}
        />
      )}
    </DashboardShell>
  );
}

function SummaryPill({ label, n, tone }) {
  const map = {
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    sky: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    slate: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${map[tone] || map.slate}`}
      data-testid={`intake-summary-${label.toLowerCase()}`}
    >
      <span className="font-semibold">{n}</span>
      <span className="text-[11px] uppercase tracking-wide">{label}</span>
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return '';
  try {
    const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 30) return `${day}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
}
